import { Fragment, useId, useState, type ReactNode } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { useNavigate } from "react-router";
import {
  FILE_STATUS_LABELS,
  FILE_STATUS_PHRASES,
  STATUS_DOUBT_LABELS,
  type DocumentStatusDoubt,
  type DocumentStatusQuestion,
  type MadeBy,
  type ProjectDocument,
} from "@gb/contracts";
import { Button } from "@/components/ui/button";
import {
  useDeleteDocument,
  useSetDocumentStatus,
} from "@/features/project/documents-api";
import { FileLink, type CameFrom } from "./file-link";

/**
 * What a file is, and whether the agent may read it (`document-ai/0013`).
 *
 * It sits above the version block because it is the bigger fact: which version
 * a file is only matters once it is this project's file at all. One real
 * project folder held 63 files from 22 other projects, which is what this
 * exists for.
 *
 * **The ordinary case is silent.** A null status means nobody has made a
 * claim — the check has not run, or it ran and the file names no project — and
 * most files name none. A block saying "not checked" on every file would make
 * the ordinary case look like a problem, which is exactly the error the null
 * status exists to avoid.
 *
 * So there are four states and two of them show almost nothing:
 *
 * - nothing at all, for a file with no claim against it, or one the model or
 *   code called this project's;
 * - one quiet line, for a file a *person* kept;
 * - the check's question, for a file it would have hidden but did not
 *   (2026-09-26): the file is still used in answers, and a person hides it or
 *   keeps it;
 * - the block that asks, for a file the agent may not read.
 *
 * Every result is said inline, beside the button that caused it. There are no
 * toasts. Keeping the file re-reads it, so the block becomes the quiet line
 * by itself.
 *
 * **Keep is the main action, and Remove is the quiet one** (2026-09-26,
 * tracker HID-12, the default the owner can change). Remove deletes the file,
 * and only a new upload brings it back. A person who trusts a wrong reason
 * could delete a file of this project with it. So Remove is a plain word
 * beside the buttons, and on a file the check was not sure of it names the
 * doubt beside it and asks a second time (`RemoveFile`).
 */
export function DocumentStatus({
  projectId,
  document,
  onRemoved,
  from,
  folded = false,
}: {
  projectId: string;
  document: ProjectDocument;
  /**
   * What happens once the file is gone. On its own screen the screen cannot
   * stay up, so it goes back to the Brain; the review after an import stays
   * where it is and lets its list re-read.
   */
  onRemoved?: () => void;
  /** Carried on the links to the files a question names, so their back link returns here. */
  from?: CameFrom;
  /**
   * Folded to one row, "This file needs a decision", that opens to the block
   * (the file's page; the product owner, 2026-09-27). The review after an
   * import shows it open, because there deciding is the task.
   */
  folded?: boolean;
}) {
  const ids = useId();
  const navigate = useNavigate();
  const { status } = document;
  const keep = useSetDocumentStatus(projectId);
  const [removing, setRemoving] = useState(false);

  // The file is gone, so its own screen cannot stay up.
  const removed = () => (onRemoved ? onRemoved() : void navigate(`/projects/${projectId}/brain/sources`));

  if (status.question) {
    return (
      <Decision folded={folded}>
        <StatusQuestion
          projectId={projectId}
          document={document}
          question={status.question}
          from={from}
          onRemoved={removed}
        />
      </Decision>
    );
  }

  if (!status.hidden || status.status === null) {
    // A person ruled on this file, and the check will never re-judge it. Said
    // once, quietly: it is the difference between a file nobody has looked at
    // and one that was flagged and kept.
    if (status.status === "this-project" && status.madeBy === "person") {
      return <p className="mt-3 text-[0.82rem] leading-[1.5] text-muted">Kept by a person.</p>;
    }
    return null;
  }

  return (
    <Decision folded={folded}>
      <div
        role="group"
        aria-labelledby={`${ids}-status`}
        className="mt-3 rounded-card-lg border-[1.5px] border-warn/40 bg-warn-bg px-4 py-3"
      >
        <p
          id={`${ids}-status`}
          className="font-display text-[0.66rem] font-semibold tracking-[0.12em] text-warn-text uppercase"
        >
          {FILE_STATUS_LABELS[status.status]}
        </p>
        <p className="mt-1 text-[0.92rem] font-semibold text-ink">
          It isn’t used in answers until someone keeps it.
        </p>
        {status.reason && <Reason madeBy={status.madeBy} reason={status.reason} />}

        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            className="hover:bg-brand-deep"
            disabled={keep.isPending || removing}
            onClick={() => keep.mutate({ documentId: document.id, status: "this-project" })}
          >
            {keep.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
            Keep it
          </Button>
          {/* A person hid it after the check asked (HID-12): the doubt is said
              beside Remove, and Remove asks a second time. */}
          <RemoveFile
            projectId={projectId}
            documentId={document.id}
            doubt={status.wasAsked}
            from={from}
            disabled={keep.isPending}
            onBusy={setRemoving}
            onStart={() => keep.reset()}
            onRemoved={removed}
          />
        </div>

        {keep.error && (
          <p role="alert" className="mt-2 text-[0.85rem] font-semibold text-danger">
            {keep.error.message}
          </p>
        )}
      </div>
    </Decision>
  );
}

/**
 * A decision the file waits for, folded to one row on the file's page. The row
 * says only that a decision is needed, in the warning colour, so it catches the
 * eye without the block's reasons and buttons filling the page. `<details>`,
 * as the Linked files section is: it stays open across the re-render an
 * answer causes.
 */
function Decision({ folded, children }: { folded: boolean; children: ReactNode }) {
  if (!folded) return <>{children}</>;
  return (
    <details className="group mt-3">
      <summary className="flex w-fit cursor-pointer list-none items-center gap-1.5 rounded-input border-[1.5px] border-warn/40 bg-warn-bg px-3 py-1.5 text-[0.84rem] font-semibold text-warn-text hover:border-warn/70">
        <ChevronDown
          size={14}
          aria-hidden
          className="shrink-0 -rotate-90 transition-transform group-open:rotate-0"
        />
        This file needs a decision
      </summary>
      {children}
    </details>
  );
}

/**
 * Remove a file the check hid or asked about (2026-09-26, tracker HID-12, the
 * default the owner can change).
 *
 * **A quiet word, not a button of the same weight as Keep.** Removing *is*
 * the delete: there is no second way to drop a file (`document-ai/0012`), and
 * only a new upload brings it back.
 *
 * It asks first, inline, as Delete asks it on the file's screen. **On a file
 * the check was not sure of, it asks a second time and names the doubt**: a
 * file with a question, or a hidden file a person hid after the check asked
 * (`doubt`). The doubt is also said beside it, before anyone presses it. A
 * hide nobody doubted asks once, as before. There is no Remove for a group.
 */
export function RemoveFile({
  projectId,
  documentId,
  doubt,
  from,
  disabled = false,
  onBusy,
  onStart,
  onRemoved,
}: {
  projectId: string;
  documentId: string;
  /** What the check was not sure of, or null for a hide nobody doubted. */
  doubt: DocumentStatusQuestion | null;
  /** Carried on the links to the files the doubt names. */
  from?: CameFrom;
  disabled?: boolean;
  /** Told while the delete is on its way, so the other answers wait. */
  onBusy?: (busy: boolean) => void;
  /** Told when a person starts to remove, so another answer's error clears. */
  onStart?: () => void;
  onRemoved: () => void;
}) {
  const ids = useId();
  const remove = useDeleteDocument(projectId);
  const [step, setStep] = useState<"idle" | "confirm" | "doubt">("idle");

  const removeNow = async (): Promise<void> => {
    onBusy?.(true);
    try {
      await remove.mutateAsync(documentId);
    } catch {
      // Said below, beside the button. Caught here so a refused delete leaves
      // the reader on the file rather than throwing out of the handler.
      onBusy?.(false);
      return;
    }
    onBusy?.(false);
    onRemoved();
  };

  const kinds = [...new Set((doubt?.doubts ?? []).map((each) => each.kind))];

  return (
    <>
      {step === "idle" && (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted hover:bg-danger-bg hover:text-danger"
            disabled={disabled || remove.isPending}
            onClick={() => {
              onStart?.();
              remove.reset();
              setStep("confirm");
            }}
          >
            Remove
          </Button>
          {doubt && (
            <span className="text-[0.78rem] font-semibold text-warn-text">
              The check was not sure{kinds.length > 0 ? `: ${kinds.map((kind) => STATUS_DOUBT_LABELS[kind]).join(" · ")}` : "."}
            </span>
          )}
        </span>
      )}
      {step === "confirm" && (
        // Inline, exactly as Delete asks it on the file's screen: a short
        // question, lighter than a dialog.
        <span className="flex items-center gap-1.5">
          <span className="text-[0.78rem] font-semibold text-danger">Remove file?</span>
          <Button variant="secondary" size="sm" disabled={remove.isPending} onClick={() => setStep("idle")}>
            No
          </Button>
          <Button
            size="sm"
            className="bg-danger hover:bg-danger"
            disabled={remove.isPending}
            onClick={() => (doubt ? setStep("doubt") : void removeNow())}
          >
            Yes
          </Button>
        </span>
      )}
      {step === "doubt" && doubt && (
        <div
          role="group"
          aria-labelledby={`${ids}-doubt`}
          className="w-full rounded-card border-[1.5px] border-danger/40 bg-surface px-3 py-2.5"
        >
          <p id={`${ids}-doubt`} className="text-[0.85rem] font-semibold text-ink">
            The check was not sure about this file.
          </p>
          {doubt.doubts.length > 0 && (
            <ul className="mt-1 flex list-disc flex-col gap-0.5 pl-5">
              {doubt.doubts.map((each, index) => (
                <li key={`${each.kind}-${index}`} className="text-[0.82rem] leading-[1.5] text-ink">
                  <DoubtSentence projectId={projectId} doubt={each} from={from} />
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-[0.82rem] leading-[1.5] text-ink-slate">
            Remove deletes the file. To get it back, you must upload it again.
          </p>
          <span className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[0.78rem] font-semibold text-danger">Remove it anyway?</span>
            <Button variant="secondary" size="sm" disabled={remove.isPending} onClick={() => setStep("idle")}>
              No
            </Button>
            <Button
              size="sm"
              className="bg-danger hover:bg-danger"
              disabled={remove.isPending}
              onClick={() => void removeNow()}
            >
              {remove.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
              Remove it
            </Button>
          </span>
        </div>
      )}
      {remove.error && (
        <p role="alert" className="w-full text-[0.85rem] font-semibold text-danger">
          {remove.error.message}
        </p>
      )}
    </>
  );
}

/**
 * The check's question about a file (decided by the product owner, 2026-09-26,
 * `document-ai/0013` §8). The check would have hidden it, but the file shares
 * something with this project, or the model was not sure, or it quoted
 * nothing from the file. So the file stays in answers, and a person decides.
 *
 * **Hide and Keep have the same weight.** It is a question, not a
 * recommendation: at the German and Austrian sets the files the guard asks about were
 * right hides and wrong hides alike. Either answer is a person's, and the
 * check never judges the file again.
 *
 * Remove is here too since 2026-09-26 (HID-12), quieter than both, and it asks
 * a second time with the doubt named: the check was not sure of this file.
 */
function StatusQuestion({
  projectId,
  document,
  question,
  from,
  onRemoved,
}: {
  projectId: string;
  document: ProjectDocument;
  question: DocumentStatusQuestion;
  from?: CameFrom;
  onRemoved: () => void;
}) {
  const ids = useId();
  const answer = useSetDocumentStatus(projectId);
  const [removing, setRemoving] = useState(false);
  const busy = answer.isPending || removing;
  return (
    <div
      role="group"
      aria-labelledby={`${ids}-question`}
      className="mt-3 rounded-card-lg border-[1.5px] border-warn/40 bg-warn-bg px-4 py-3"
    >
      <p className="font-display text-[0.66rem] font-semibold tracking-[0.12em] text-warn-text uppercase">
        Hide or keep?
      </p>
      <p id={`${ids}-question`} className="mt-1 text-[0.92rem] font-semibold text-ink">
        Is this {FILE_STATUS_PHRASES[question.status]}?
      </p>
      <p className="mt-1 text-[0.82rem] leading-[1.5] text-ink-slate">
        The check would have hidden it, but it was not sure. It is used in answers until someone
        decides.
      </p>
      {question.reason && (
        <p className="mt-1 text-[0.82rem] leading-[1.5] text-ink-slate">The model’s reason: {question.reason}</p>
      )}
      {question.evidence && (
        <p className="mt-1 text-[0.82rem] leading-[1.5] text-ink-slate">
          It quoted the file: “{question.evidence}”
        </p>
      )}
      {question.doubts.length > 0 && (
        <ul aria-label="Why it was not hidden" className="mt-1.5 flex list-disc flex-col gap-0.5 pl-5">
          {question.doubts.map((doubt, index) => (
            <li key={`${doubt.kind}-${index}`} className="text-[0.82rem] leading-[1.5] text-ink">
              <DoubtSentence projectId={projectId} doubt={doubt} from={from} />
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => answer.mutate({ documentId: document.id, status: question.status })}
        >
          {busy && answer.variables?.status === question.status ? (
            <Loader2 size={14} className="animate-spin" />
          ) : null}
          Hide it
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => answer.mutate({ documentId: document.id, status: "this-project" })}
        >
          {busy && answer.variables?.status === "this-project" ? (
            <Loader2 size={14} className="animate-spin" />
          ) : null}
          Keep it
        </Button>
        <RemoveFile
          projectId={projectId}
          documentId={document.id}
          doubt={question}
          from={from}
          disabled={answer.isPending}
          onBusy={setRemoving}
          onStart={() => answer.reset()}
          onRemoved={onRemoved}
        />
      </div>
      {answer.error && (
        <p role="alert" className="mt-2 text-[0.85rem] font-semibold text-danger">
          {answer.error.message}
        </p>
      )}
    </div>
  );
}

/**
 * One reason the check asked, as a sentence. The files of this project that
 * share it are named and linked, so a person can look at both.
 */
export function DoubtSentence({
  projectId,
  doubt,
  from,
}: {
  projectId: string;
  doubt: DocumentStatusDoubt;
  from?: CameFrom;
}) {
  const others =
    doubt.documents.length === 0 ? (
      "a file of this project does"
    ) : (
      <>
        {doubt.documents.map((other, index) => (
          <Fragment key={other.id}>
            {index > 0 ? (index === doubt.documents.length - 1 ? " and " : ", ") : null}“
            <FileLink projectId={projectId} id={other.id} from={from}>
              {other.name}
            </FileLink>
            ”
          </Fragment>
        ))}
        {doubt.documents.length === 1 ? " does" : " do"}
      </>
    );
  const sentence: Record<DocumentStatusDoubt["kind"], ReactNode> = {
    "shares-party": (
      <>
        It names “{doubt.value}”, as {others}.
      </>
    ),
    "shares-number": (
      <>
        It holds the number “{doubt.value}”, as {others}.
      </>
    ),
    "names-this-project": <>It names this project as “{doubt.value}”.</>,
    "not-sure": "The model said it was not sure.",
    "no-evidence": "The model quoted nothing from the file itself that shows it.",
    // 2026-09-26 (HID-10): hiding the newest version makes an older one current.
    "looks-like-version": (
      <>
        It has “{doubt.value}”, as {others}. It may be another version of the same document.
      </>
    ),
    // 2026-09-26 (HID-15): code asks this, not the model.
    "inside-hidden-file": (
      <>
        It came in “
        {doubt.documents[0] ? (
          <FileLink projectId={projectId} id={doubt.documents[0].id} from={from}>
            {doubt.documents[0].name}
          </FileLink>
        ) : (
          doubt.value
        )}
        ”, which is hidden. The check found this file to be this project’s on its own.
      </>
    ),
    // 2026-09-26 (LIN-02): the same bytes keep one status, and code asks this.
    "copy-filed-elsewhere": (
      <>
        The same file is also filed in “{doubt.value}”
        {doubt.documents[0] ? (
          <>
            , as “
            <FileLink projectId={projectId} id={doubt.documents[0].id} from={from}>
              {doubt.documents[0].name}
            </FileLink>
            ”
          </>
        ) : null}
        . That folder may say something else about it.
      </>
    ),
    // 2026-09-26 (LIN-03): the model said which entry of a list is this file.
    "listed-by-this-project": (
      <>
        {doubt.documents[0] ? (
          <>
            “
            <FileLink projectId={projectId} id={doubt.documents[0].id} from={from}>
              {doubt.documents[0].name}
            </FileLink>
            ”
          </>
        ) : (
          "A file of this project"
        )}{" "}
        lists it as “{doubt.value}”.
      </>
    ),
  };
  return <>{sentence[doubt.kind]}</>;
}

/** The one sentence whoever set the status wrote, and who wrote it. */
function Reason({ madeBy, reason }: { madeBy: MadeBy | null; reason: string }) {
  const lead = reasonLead(madeBy);
  return (
    <p className="mt-1 text-[0.82rem] leading-[1.5] text-ink-slate">
      {lead ? `${lead} ${reason}` : reason}
    </p>
  );
}

/**
 * Whose sentence this is.
 *
 * The reason is quoted, not paraphrased, so it has to say who wrote it. The
 * same three authors as a placement's, and the same wording for the model's,
 * because a reader meets both on this screen (`document-ai/0016`). A status
 * with no author is not attributed to one: the sentence stands on its own.
 */
function reasonLead(madeBy: MadeBy | null): string | null {
  switch (madeBy) {
    case "model":
      return "The model’s reason:";
    case "person":
      return "A person’s reason:";
    case "code":
      return "Found by code:";
    default:
      return null;
  }
}
